import { useQuery } from "@tanstack/react-query";

import { translate } from "app/filters/i18nFilter";
import type { GaErrorKind } from "app/modules/ga-connect/classifyGaError";
import { classifyGaError } from "app/modules/ga-connect/classifyGaError";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { createActivationApi } from "app/pages/activation/services/api";
import { ResponseError } from "app/services/fetchService/defaults";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import type { FirstPartyDataSource, GADataSchema } from "../../types/api";

export const GA_DATA_QUERY_KEY = "ga-activation-data";

const useGAData = (
    objectId: string,
    granularity: GranularityNames,
    enabled: boolean,
    source?: FirstPartyDataSource,
) => {
    const { showErrorToast } = useToasts();

    const query = useQuery<GADataSchema>({
        queryKey: [GA_DATA_QUERY_KEY, objectId, granularity, source],
        queryFn: async () => {
            try {
                const api = createActivationApi();

                return await api.ga.getGAData(objectId, granularity, source);
            } catch (error) {
                showErrorToast(translate("common.api.error", { error: error?.message }));
                throw error;
            }
        },
        refetchOnMount: true,
        enabled: enabled && !!objectId && !!source,
        retry: false,
    });

    const gaErrorKind: GaErrorKind | undefined =
        query.error instanceof ResponseError
            ? classifyGaError(query.error.status, query.error.problem?.type)
            : query.error
              ? "unknown"
              : undefined;

    return {
        ...query,
        gaErrorKind,
    };
};

export default useGAData;
